import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const money = (v) => `£${Number(v ?? 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

const useList = (key, enabled) =>
  useQuery({ queryKey: ["admin", key], queryFn: async () => (await api.get(`/admin/${key}`)).data, enabled, refetchInterval: 30000 });

const StatusSelect = ({ target, id, value, options }) => {
  const qc = useQueryClient();
  const change = async (e) => {
    try {
      await api.patch(`/admin/${target}/${id}/status`, { status: e.target.value });
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("Status updated");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <select value={value || options[0]} onChange={change} className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold" data-testid={`status-select-${target}-${id}`}>
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  );
};

const Table = ({ cols, rows, empty, testId }) => (
  <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white" data-testid={testId}>
    <table className="w-full min-w-[760px] text-left text-sm">
      <thead className="bg-[#FAF8F5] text-xs uppercase tracking-wider text-stone-600">
        <tr>{cols.map((c) => <th key={c.h} className="px-4 py-3 font-semibold">{c.h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-stone-600">{empty}</td></tr>}
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-stone-100 align-top">
            {cols.map((c) => <td key={c.h} className="px-4 py-3">{c.r(r)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const downloadCv = async (row) => {
  try {
    const res = await api.get(`/admin/chefs/${row.id}/cv`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = row.cv_filename || "cv";
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const Who = ({ a, b }) => (<><p className="font-semibold">{a}</p><p className="text-xs text-stone-600">{b}</p></>);

const COLS = {
  enquiries: [
    { h: "Date", r: (r) => fmtDate(r.created_at) },
    { h: "Organisation", r: (r) => <Who a={r.organisation} b={r.contact_name} /> },
    { h: "Contact", r: (r) => <Who a={r.email} b={r.phone || "—"} /> },
    { h: "Sector / plan", r: (r) => <Who a={r.sector} b={r.plan || "Not sure"} /> },
    { h: "Size", r: (r) => <Who a={`${r.sites ?? "—"} sites`} b={`${r.meals_per_day ?? "—"} meals/day`} /> },
    { h: "Message", r: (r) => <p className="max-w-[240px] text-xs text-stone-700">{r.message || "—"}</p> },
    { h: "Status", r: (r) => <StatusSelect target="enquiries" id={r.id} value={r.status} options={["new", "contacted", "proposal sent", "won", "lost"]} /> },
  ],
  subscriptions: [
    { h: "Date", r: (r) => fmtDate(r.created_at) },
    { h: "Organisation", r: (r) => <Who a={r.organisation} b={r.contact_name} /> },
    { h: "Contact", r: (r) => <Who a={r.email} b={r.phone || "—"} /> },
    { h: "Plan", r: (r) => <Who a={r.plan} b={r.sector} /> },
    { h: "Monthly", r: (r) => money(r.amount) },
    { h: "Payment", r: (r) => <span className={`rounded-full px-3 py-1 text-xs font-semibold ${r.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-700"}`}>{r.payment_status}</span> },
  ],
  orders: [
    { h: "Date", r: (r) => fmtDate(r.created_at) },
    { h: "Customer", r: (r) => <Who a={r.customer_name || "—"} b={r.customer_email || ""} /> },
    { h: "Items", r: (r) => (r.items ?? []).map((i) => <p key={i.name} className="text-xs">{i.quantity} × {i.name}</p>) },
    { h: "Ship to", r: (r) => <p className="text-xs">{r.shipping ? [r.shipping.line1, r.shipping.city, r.shipping.postal_code].filter(Boolean).join(", ") : "—"}</p> },
    { h: "Total", r: (r) => money(r.amount_paid || r.amount) },
    { h: "Payment", r: (r) => <span className={`rounded-full px-3 py-1 text-xs font-semibold ${r.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-700"}`}>{r.payment_status}</span> },
    { h: "Fulfilment", r: (r) => <StatusSelect target="orders" id={r.id} value={r.fulfilment} options={["new", "packed", "shipped", "delivered"]} /> },
  ],
  downloads: [
    { h: "Date", r: (r) => fmtDate(r.created_at) },
    { h: "Customer", r: (r) => <Who a={r.customer_name || "—"} b={r.customer_email || ""} /> },
    { h: "Document", r: (r) => (r.items ?? []).map((i) => <p key={i.name} className="text-xs">{i.name}</p>) },
    { h: "Total", r: (r) => money(r.amount_paid || r.amount) },
    { h: "Downloads", r: (r) => r.download_count ?? 0 },
    { h: "Emailed", r: (r) => (r.email_sent ? "Yes" : r.payment_status === "paid" ? "Not sent" : "—") },
    { h: "Payment", r: (r) => <span className={`rounded-full px-3 py-1 text-xs font-semibold ${r.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-700"}`}>{r.payment_status}</span> },
  ],
  chefs: [
    { h: "Date", r: (r) => fmtDate(r.created_at) },
    { h: "Name", r: (r) => <Who a={r.full_name} b={r.location} /> },
    { h: "Contact", r: (r) => <Who a={r.email} b={r.phone} /> },
    { h: "Role", r: (r) => <Who a={r.role} b={`${r.experience_years} yrs · ${r.availability}`} /> },
    { h: "CV", r: (r) => <button onClick={() => downloadCv(r)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#9a7614] hover:underline" data-testid={`chef-cv-download-${r.id}`}><Download className="h-3.5 w-3.5" /> {r.cv_filename}</button> },
    { h: "Status", r: (r) => <StatusSelect target="chefs" id={r.id} value={r.status} options={["new", "reviewing", "interview", "approved", "rejected"]} /> },
  ],
};

const TABS = [
  { k: "enquiries", label: "Enquiries", empty: "No enquiries yet." },
  { k: "subscriptions", label: "Subscriptions", empty: "No subscriptions yet." },
  { k: "orders", label: "Flapjack orders", empty: "No orders yet." },
  { k: "chefs", label: "Chef applications", empty: "No applications yet." },
  { k: "downloads", label: "Document sales", empty: "No document sales yet." },
];

export default function Admin() {
  const nav = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [params] = useSearchParams();
  const tabParam = params.get("tab");
  const initialTab = TABS.some((t) => t.k === tabParam) ? tabParam : "enquiries";

  useEffect(() => {
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => nav(tabParam ? `/admin/login?tab=${encodeURIComponent(tabParam)}` : "/admin/login", { replace: true }));
  }, [nav, tabParam]);

  const summary = useQuery({ queryKey: ["admin", "summary"], queryFn: async () => (await api.get("/admin/summary")).data, enabled: !!authed });
  const lists = { enquiries: useList("enquiries", !!authed), subscriptions: useList("subscriptions", !!authed), orders: useList("orders", !!authed), chefs: useList("chefs", !!authed), downloads: useList("downloads", !!authed) };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => null);
    nav("/admin/login");
  };

  if (!authed) return <div className="grid min-h-screen place-items-center bg-[#141412] text-stone-300">Loading…</div>;
  const s = summary.data ?? {};
  const kpis = [
    { k: "enquiries", label: "Contract enquiries", v: s.enquiries ?? 0, sub: `${s.new_enquiries ?? 0} new` },
    { k: "subscriptions", label: "Paid subscriptions", v: s.subscriptions ?? 0 },
    { k: "orders", label: "Paid Flapjack orders", v: s.orders ?? 0 },
    { k: "chefs", label: "Chef applications", v: s.chefs ?? 0 },
    { k: "downloads", label: "Document sales", v: s.downloads ?? 0 },
  ];

  return (
    <div className="min-h-screen bg-[#EFEAE1]" data-testid="admin-dashboard">
      <header className="bg-[#141412]">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" data-testid="admin-home-link"><Logo /></Link>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/10" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-5 py-10 sm:px-8">
        <h1 className="font-display text-4xl font-bold sm:text-5xl">Dashboard</h1>
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-5">
          {kpis.map((k) => (
            <div key={k.k} className="rounded-2xl bg-white p-5" data-testid={`kpi-${k.k}`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-600">{k.label}</p>
              <p className="mt-2 font-display text-4xl font-bold">{k.v}</p>
              {k.sub && <p className="text-xs text-[#9a7614]">{k.sub}</p>}
            </div>
          ))}
        </div>
        <Tabs defaultValue={initialTab} className="mt-10">
          <TabsList className="h-auto flex-wrap bg-white p-1">
            {TABS.map((t) => <TabsTrigger key={t.k} value={t.k} className="data-[state=active]:bg-[#141412] data-[state=active]:text-white" data-testid={`admin-tab-${t.k}`}>{t.label}</TabsTrigger>)}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t.k} value={t.k} className="mt-6">
              <Table cols={COLS[t.k]} rows={Array.isArray(lists[t.k].data) ? lists[t.k].data : []} empty={lists[t.k].isLoading ? "Loading…" : t.empty} testId={`admin-table-${t.k}`} />
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}
